import { useState, type ReactNode } from 'react';
import { ArrowRightIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { Logo } from '../../design-system';
import { useAuth } from '../auth/useAuth';
import {
  TekeningBeginnen,
  TekeningBestand,
  TekeningGesprekken,
  TekeningHulpvragen,
  TekeningOpnemen,
  TekeningTranscript,
  TekeningVerder,
  TekeningWaarvoor,
} from './tekeningen';

const TEAMS_UITLEG =
  'https://support.microsoft.com/nl-nl/teams/meetings/start-stop-and-find-meeting-recordings-in-microsoft-teams';

interface Stap {
  /** De naam onder het nummer, en de naam van het venster voor een schermlezer. */
  naam: string;
  /** Alleen op het welkom: het merk in beeld in plaats van een tekening. */
  welkom?: boolean;
  tekening?: ReactNode;
  tekst: ReactNode;
  knop?: string;
}

/**
 * De uitleg, in negen stappen, voor iedereen die kan inloggen.
 *
 * De vorm komt uit het ontwerp: een liggende kaart met links de tekst en rechts
 * het beeld, en onderaan het nummer, de stippen en de knop. Op het welkom staat
 * rechts de facet-V uit de huisstijl als watermerk, en dat is dezelfde tekening
 * als het logo linksboven — vandaar dat hij licht staat.
 *
 * Twee dingen staan er bewust in die niet over bediening gaan. Dat de opname en
 * de letterlijke tekst weggaan zodra de hulpvragen zijn vastgesteld, want dat is
 * wat een buddy een collega wil kunnen vertellen vóórdat hij op opnemen drukt.
 * En dat er geen namen in een hulpvraag horen, want die zinnen lezen straks al
 * zijn collega's.
 *
 * De tekst schrijft de lezer geen rol toe: aanleveren is van het buddyvinkje en
 * kijken van iedereen, en stap 4 zegt vanaf waar het over aanleveren gaat. Zo
 * klopt hij ook voor een Admin of een View Only, die hem nu ook krijgt.
 */
function stappen(): Stap[] {
  return [
    {
      naam: 'Welkom bij VIP',
      welkom: true,
      knop: 'Start',
      tekst: (
        <p className="text-muted-foreground text-[1.0625rem]">
          We nemen je in een paar korte stappen mee door de app.
        </p>
      ),
    },
    {
      naam: 'Introductie VIP',
      tekening: <TekeningWaarvoor />,
      tekst: (
        <>
          <Regel>In buddygesprekken tussen collega&rsquo;s komen hulpvragen naar boven.</Regel>
          <Regel>VIP haalt die hulpvragen er automatisch uit, geen aparte tools meer.</Regel>
          <Regel>
            Bij elkaar wordt dat één beeld van waar collega&rsquo;s tegenaan lopen, en op basis
            daarvan wordt bepaald welke kennis er ingekocht moet worden.
          </Regel>
        </>
      ),
    },
    {
      naam: 'Mijn gesprekken',
      tekening: <TekeningGesprekken />,
      tekst: (
        <>
          <Regel>
            In de zijbalk staat Mijn gesprekken. Daar staat elk gesprek als een kaart, met vier
            stappen: opname, transcript, hulpvragen, klaar.
          </Regel>
          <Regel>
            Staat er &quot;Jij bent aan zet&quot;, dan wacht het proces op jouw validatie.
          </Regel>
          <Regel>Lever je zelf geen gesprekken aan, dan blijft de lijst leeg.</Regel>
        </>
      ),
    },
    {
      naam: 'Een gesprek beginnen',
      tekening: <TekeningBeginnen />,
      tekst: (
        <>
          <Regel>
            De volgende stappen gaan over het aanleveren van een gesprek. Dat doet wie
            buddygesprekken voert; de rest leest mee.
          </Regel>
          <Regel>
            Het begint met een naam voor het gesprek en de datum waarop het plaatsvond. Daarna kies
            je wie eraan meededen. Wie er nog niet in staat, voeg je meteen toe, met niveau en
            opdracht erbij.
          </Regel>
        </>
      ),
    },
    {
      naam: 'Opnemen vanuit de app',
      tekening: <TekeningOpnemen />,
      tekst: (
        <>
          <Regel>
            Je kunt een gesprek rechtstreeks vanuit de app opnemen. Daar heb je verder niets voor
            nodig.
          </Regel>
          <Regel>
            Druk op opnemen en begin te praten. De meter laat zien dat er geluid binnenkomt, je kunt
            tussendoor pauzeren, en met stoppen is de opname klaar om te versturen. Houd het tabblad
            open zolang je bezig bent.
          </Regel>
          <Regel>
            Op een iPhone stopt de opname zodra het scherm op slot gaat. Neem je op met je telefoon
            in je zak, lever dan een bestand aan; hoe dat gaat lees je op de volgende stap.
          </Regel>
        </>
      ),
    },
    {
      naam: 'Of lever een bestand aan',
      tekening: <TekeningBestand />,
      tekst: (
        <>
          <Regel>
            Je kunt ook een geluidsbestand aanleveren. Hoe dat is opgenomen maakt voor de app niet
            uit.
          </Regel>
          <Regel>
            Neem het gesprek op met de recorder die al op je telefoon of laptop staat. Die blijft
            doorlopen als het scherm uitgaat.
          </Regel>
          <Regel>
            Of neem het gesprek op in Teams. Zet daarbij alleen audio aan; meer heeft de app niet
            nodig.
          </Regel>
          <p className="text-[1.0625rem]">
            <a
              className="text-primary underline"
              href={TEAMS_UITLEG}
              rel="noreferrer"
              target="_blank"
            >
              Microsoft legt uit waar die knop zit.
            </a>
          </p>
        </>
      ),
    },
    {
      naam: 'Het transcript controleren',
      tekening: <TekeningTranscript />,
      tekst: (
        <>
          <Regel>
            Van het gesprek maakt de app een samenvatting van ongeveer een vijfde van de lengte. Die
            controleer je, waarna je kunt aanpassen wat er verkeerd is beschreven.
          </Regel>
          <Regel>
            Zodra je verderop de hulpvragen hebt vastgesteld, gaan de opname en de letterlijke tekst
            weg. De samenvatting blijft bewaard.
          </Regel>
        </>
      ),
    },
    {
      naam: 'Hulpvragen beoordelen',
      tekening: <TekeningHulpvragen />,
      tekst: (
        <>
          <Regel>
            De app haalt de hulpvragen uit de samenvatting en ordent ze per onderwerp. Jij past aan
            wat er niet klopt, haalt weg wat er niet bij hoort en vult aan wat er mist. Daarna maak
            jij de hulpvragen definitief.
          </Regel>
          <p className="font-heading mt-4 mb-1.5 text-xl font-bold">Let op</p>
          <Regel>
            Zet geen namen in de hulpvragen. De zinnen worden straks ook door collega&rsquo;s
            gelezen. Staat er een naam in die de app kent, dan krijg je een waarschuwing.
          </Regel>
        </>
      ),
    },
    {
      naam: 'Overige schermen',
      tekening: <TekeningVerder />,
      knop: 'Aan de slag',
      tekst: (
        <>
          <Regel>
            Dashboard: waar collega&rsquo;s het vaakst tegenaan lopen, over alle gesprekken heen.
          </Regel>
          <Regel>Personen: wie er zijn, op welke opdracht ze zitten en wie hen begeleidt.</Regel>
          <Regel>Begeleiding: het bord met wie wie begeleidt.</Regel>
          <Regel>Hulpvragen: alle onderwerpen bij elkaar, met de zinnen die eronder hangen.</Regel>
        </>
      ),
    },
  ];
}

/** Eén alinea. Eigen component zodat de maat en de tussenruimte overal gelijk
 *  zijn; dit is de lopende tekst uit het ontwerp. */
function Regel({ children }: { children: ReactNode }) {
  return <p className="text-muted-foreground max-w-[26rem] text-[1.0625rem]">{children}</p>;
}

/** Het welkom: het merk groot in beeld met de groet eronder. */
function Welkom({ titel, voornaam }: { titel: ReactNode; voornaam: string }) {
  return (
    <>
      {titel}
      {/* `self-start` is geen opmaak maar een reparatie: dit staat in een
          flexkolom, en die rekt zijn kinderen standaard op tot de volle
          breedte. Met een vaste hoogte erbij trekt dat het logo scheef. */}
      <span className="self-start">
        <Logo height={52} />
      </span>
      <p className="text-primary text-[0.8125rem] font-bold tracking-[0.12em] uppercase">
        Welkom bij VIP
      </p>
      <p className="font-heading text-[2.5rem] leading-[1.1] font-bold tracking-tight text-balance">
        Hoi {voornaam},
        <br />
        fijn dat je er bent.
      </p>
    </>
  );
}

/**
 * Het venster.
 *
 * `onSluit` vuurt hoe je ook weggaat, ook bij de kruisknop halverwege. Wie hem
 * heeft geopend heeft hem gehad; blijven tonen tot iemand netjes doorklikt maakt
 * van een uitleg een blokkade.
 */
export function Uitleg({ open, onSluit }: { open: boolean; onSluit: () => void }) {
  const telefoon = useIsMobile();
  const { user } = useAuth();
  const [stap, setStap] = useState(0);

  const voornaam = (user?.naam ?? '').split(' ')[0] ?? '';
  const alle = stappen();
  const huidig = alle[stap]!;
  const laatste = stap === alle.length - 1;

  function sluit() {
    onSluit();
    // Terug naar het begin, maar pas nadat het venster dicht is: anders flitst
    // de eerste stap nog even langs tijdens het sluiten.
    setTimeout(() => setStap(0), 200);
  }

  const Inhoud = ({ Titel }: { Titel: React.ElementType }) => (
    <div className="relative flex flex-col gap-4 md:gap-6">
      {/* Eén vaste hoogte voor het bovenvlak, zoals in het ontwerp: dan is elke
          stap even groot en verspringt het venster niet bij elke klik. De maat
          is die van de volste kaart; de rest houdt ruimte over. Op een telefoon
          niet, daar schuift het blad mee met de tekst. */}
      <div className="grid gap-4 md:h-[23.5rem] md:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] md:gap-6">
        <div className="flex flex-col justify-center gap-5">
          {huidig.welkom ? (
            <Welkom titel={<Titel className="sr-only">{huidig.naam}</Titel>} voornaam={voornaam} />
          ) : (
            <Titel className="font-heading text-3xl leading-tight font-bold tracking-tight text-balance">
              {huidig.naam}
            </Titel>
          )}
          <div className="flex flex-col gap-3">{huidig.tekst}</div>
        </div>

        {huidig.welkom ? (
          // Het watermerk loopt de kaart uit, net als in het ontwerp. Dezelfde
          // tekening als het logo, dus hij staat licht: anders krijg je twee
          // merken die om aandacht vragen.
          <img
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -top-7 -right-28 hidden w-80 opacity-[0.11] md:block"
            src="/safari-pinned-tab.svg"
          />
        ) : (
          <div className="flex items-center justify-center *:max-w-56 md:*:max-w-none">
            {huidig.tekening}
          </div>
        )}
      </div>

      {/* Twee kolommen, ook op een telefoon: het nummer links en de knoppen
          rechts. `h-8` is de hoogte van een knop, en die staat op alle drie de
          blokken zodat het nummer, de stippen en de knoppen op één lijn liggen.
          De naam van de stap hangt daaronder. */}
      <div className="grid grid-cols-[1fr_auto] items-start gap-4 sm:grid-cols-[1fr_auto_1fr]">
        <div>
          {/* `gap-1` vervangt de spatie tussen de twee delen: in een flexregel
              telt los wit tussen twee elementen niet mee. */}
          <p className="flex h-8 items-center gap-1 text-[1.0625rem] font-bold tabular-nums">
            <span className="text-primary">{String(stap + 1).padStart(2, '0')}</span>
            <span className="text-muted-foreground text-sm font-normal">
              / {String(alle.length).padStart(2, '0')}
            </span>
          </p>
          <p className="text-muted-foreground text-[0.8125rem]">{huidig.naam}</p>
        </div>

        <div className="flex h-8 items-center justify-center gap-2 max-sm:order-3 max-sm:col-span-full">
          {alle.map((s, i) => (
            <span
              className={`size-2 rounded-full ${i === stap ? 'bg-primary' : 'bg-muted'}`}
              key={s.naam}
            />
          ))}
        </div>

        <div className="flex h-8 items-center justify-end gap-3">
          {stap > 0 && (
            <Button onClick={() => setStap(stap - 1)} variant="ghost">
              Vorige
            </Button>
          )}
          <Button onClick={() => (laatste ? sluit() : setStap(stap + 1))}>
            {huidig.knop ?? 'Volgende'}
            <ArrowRightIcon data-icon="inline-end" />
          </Button>
        </div>
      </div>
    </div>
  );

  if (telefoon) {
    return (
      <Sheet onOpenChange={(o) => !o && sluit()} open={open}>
        {/* `max-h` is hier geen smaak maar een reparatie: zonder maximum groeit
            het blad tot boven het scherm uit en doet `overflow-y` niets, want er
            valt niets te schuiven. Dan staat de kop van de stap boven de rand.
            `svh` en niet `vh`, anders rekent hij met een scherm zonder de balken
            van de browser en valt de knop er alsnog onder. */}
        <SheetContent
          className="max-h-[94svh] gap-0 overflow-x-hidden overflow-y-auto px-5 pt-6 pb-5"
          side="bottom"
        >
          <SheetDescription className="sr-only">Uitleg over de app</SheetDescription>
          <Inhoud Titel={SheetTitle} />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog onOpenChange={(o) => !o && sluit()} open={open}>
      <DialogContent className="gap-0 overflow-hidden px-12 pt-11 pb-8 sm:max-w-[52rem]">
        <DialogDescription className="sr-only">Uitleg over de app</DialogDescription>
        <Inhoud Titel={DialogTitle} />
      </DialogContent>
    </Dialog>
  );
}
